import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getIn, useFormikContext } from 'formik';
import { Box, Button, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Text } from '@app/ui/forms';
import { Tooltip } from '@app/ui/tooltip';

import {
  MATCHING_ENTITY_VALUES,
  MATCHING_NOTES_SECTION_LABEL_KEYS,
  MATCHING_NOTES_SECTION_VALUES,
} from '../../constants';
import { MatchingEntity, MatchingNotesSection, MatchingSourceKind, RuleField } from '../../types';
import { emptyMatchingSourceRow, MatchingSourceFormValue, matchingSourceRowError } from '../../utils';
import { MatchingFieldAutocomplete } from './MatchingFieldAutocomplete';

export interface MatchingSourcePickerProps {
  readonly name: string;
  readonly fields: RuleField[];
  readonly disabled: boolean;
}

// One row of a matching rule's `sources[]` (SPEC §6.3): entity picker first (which narrows the
// field list), then EITHER a catalogue field OR a Notes-section name — the second is a distinct
// "source kind" toggle, not another field on the same picker. Mirrors `ColorCaseRow`'s shape, one
// entity narrower.
export const MatchingSourcePicker = ({ name, fields, disabled }: MatchingSourcePickerProps) => {
  const { t } = useTranslation('common');
  const { values, setFieldValue } = useFormikContext<unknown>();

  const rows = (getIn(values, name) as MatchingSourceFormValue[] | undefined) ?? [];

  const replaceRows = (next: MatchingSourceFormValue[]) => setFieldValue(name, next);

  const fieldsByEntity = useMemo(() => {
    const map = new Map<MatchingEntity, RuleField[]>();

    MATCHING_ENTITY_VALUES.forEach(entity =>
      map.set(
        entity,
        fields.filter(field => field.entity === entity),
      ),
    );

    return map;
  }, [fields]);

  // A saved heading the engine does not recognise stays selected (listed as "not recognised") rather
  // than blanking silently; the row error blocks saving until one of the known headings is picked.
  const notesSectionOptions = (current: string) => [
    ...MATCHING_NOTES_SECTION_VALUES.map(section => ({
      id: section,
      name: t(MATCHING_NOTES_SECTION_LABEL_KEYS[section as MatchingNotesSection]) ?? section,
    })),
    ...(current && !MATCHING_NOTES_SECTION_VALUES.includes(current)
      ? [{ id: current, name: t('rules.matching.source.unknownNotesSectionOption', { name: current }) ?? current }]
      : []),
  ];

  return (
    <Box>
      {!rows.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('rules.matching.source.empty')}
        </Typography>
      )}

      {rows.map((row, index) => {
        const rowName = `${name}.${index}`;
        const entityFields = row.entity ? fieldsByEntity.get(row.entity) ?? [] : [];
        const selectedField = entityFields.find(field => field.path === row.field) ?? null;
        const error = matchingSourceRowError(row, key => t(key) ?? key);

        const handleEntityChange = (entity: MatchingEntity | '') =>
          setFieldValue(rowName, { ...row, entity, field: '' });

        return (
          <Box key={row.uid} sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: '16px', p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h4">{t('rules.matching.source.heading', { number: index + 1 })}</Typography>

              <Tooltip title={t('rules.editor.condition.removeButton') ?? undefined}>
                <span>
                  <IconButton
                    size="small"
                    disabled={disabled || rows.length <= 1}
                    onClick={() => replaceRows(rows.filter((_row, rowIndex) => rowIndex !== index))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ width: { xs: '100%', sm: 140 } }}>
                <Text
                  select
                  disabled={disabled}
                  label={t('rules.matching.source.entityLabel')}
                  value={row.entity}
                  onChange={event => handleEntityChange(event.target.value as MatchingEntity)}
                  options={MATCHING_ENTITY_VALUES.map(entity => ({
                    id: entity,
                    name: t(`rules.matching.entity.option.${entity}`) ?? entity,
                  }))}
                />
              </Box>

              <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                <Text
                  select
                  disabled={disabled}
                  label={t('rules.matching.source.kindLabel')}
                  value={row.sourceKind}
                  onChange={event =>
                    setFieldValue(rowName, {
                      ...row,
                      sourceKind: event.target.value as MatchingSourceKind,
                      field: '',
                      notesSection: '',
                    })
                  }
                  options={[
                    {
                      id: MatchingSourceKind.field,
                      name: t('rules.matching.source.kind.field') ?? MatchingSourceKind.field,
                    },
                    {
                      id: MatchingSourceKind.notes,
                      name: t('rules.matching.source.kind.notes') ?? MatchingSourceKind.notes,
                    },
                  ]}
                />
              </Box>

              <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                {row.sourceKind === MatchingSourceKind.field ? (
                  <MatchingFieldAutocomplete
                    disabled={disabled || !row.entity}
                    fields={entityFields}
                    value={selectedField}
                    onChange={next => setFieldValue(rowName, { ...row, field: next?.path ?? '' })}
                    label={t('rules.editor.condition.fieldLabel')}
                    helperText={t('rules.editor.condition.fieldHelperText')}
                  />
                ) : (
                  <Text
                    select
                    disabled={disabled}
                    label={t('rules.matching.source.notesSectionLabel')}
                    helperText={t('rules.matching.source.notesSectionHelperText')}
                    value={row.notesSection}
                    onChange={event => setFieldValue(rowName, { ...row, notesSection: event.target.value })}
                    options={notesSectionOptions(row.notesSection)}
                  />
                )}
              </Box>
            </Box>

            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
        );
      })}

      {!disabled && (
        <Button size="small" startIcon={<AddIcon />} onClick={() => replaceRows([...rows, emptyMatchingSourceRow()])}>
          {t('rules.matching.source.addButton')}
        </Button>
      )}
    </Box>
  );
};
